/* global __BUILD_ID__ */ // lo inyecta vite.config en tiempo de build
import { useState, useEffect, useCallback } from 'react'
import api from '../api/client'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import Boton from './Boton'
import { text } from '../design/tokens'

/**
 * Avisa cuando se deployó una versión nueva mientras el usuario tenía la
 * pestaña abierta: su pantalla sigue corriendo el bundle viejo contra un
 * backend nuevo, y puede seguir así todo el día (se deploya casi siempre en
 * horario laboral).
 *
 * NO recarga sola y NO bloquea la pantalla: solo avisa y deja el botón. La
 * versión del 21 ago se recargaba sola cuando "parecía seguro" (sin trabajo sin
 * guardar y con el usuario quieto) y a Martin no le gustó — con razón: que la
 * pantalla se mueva sola es peor que trabajar un rato más con la versión
 * anterior. Cuándo actualizar lo decide la persona.
 *
 * Trabajo en curso (sugerencia #102, 20 oct 2026): si la pantalla registró
 * trabajo sin guardar/despachar (`useDirtyGuard` → window.__lgrDirty: form de
 * NV, presupuestos, cobros, Asignación de salidas…), "Actualizar ahora" lo
 * dice y pide confirmación. Mismo idioma que el resto del guard (confirm).
 */
const MI_VERSION = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : null

// "…¿Salir igual?" es la pregunta del guard al navegar; acá la pregunta es otra.
const sinPregunta = (msg) => String(msg).replace(/\s*¿[^¿?]*\?\s*$/, '')

function actualizar() {
  const trabajo = window.__lgrDirty
  if (trabajo && !window.confirm(
    `Hay trabajo en curso en esta pantalla:\n\n${sinPregunta(trabajo)}\n\n¿Actualizar igual?`
  )) return
  window.__lgrDirty = null   // ya confirmó: que el beforeunload no vuelva a preguntar
  window.location.reload()
}

export default function AvisoVersionNueva() {
  const [hayVersionNueva, setHayVersionNueva] = useState(false)

  const consultar = useCallback(() => {
    api.get('/sistema/version')
      .then(({ data }) => {
        // Sin versión de un lado o del otro (dev, o un build sin el archivo) no
        // se compara: mejor no avisar que avisar de más.
        if (!data.version || !MI_VERSION) return
        setHayVersionNueva(data.version !== MI_VERSION)
      })
      .catch(() => {}) // sin conexión: el cartel no es el problema del momento
  }, [])

  useEffect(() => { consultar() }, [consultar])
  useAutoRefresh(consultar, 30000)

  if (!hayVersionNueva) return null

  return (
    <div style={st.barra} role="alert">
      <span style={st.icono}>✨</span>
      <div style={{ flex: 1 }}>
        <div style={st.titulo}>Hay una versión nueva de LGR</div>
        <div style={st.detalle}>
          Esta pantalla está trabajando con la anterior. Actualizá cuando termines
          lo que estés haciendo — no se pierde nada de lo ya guardado.
        </div>
      </div>
      <Boton variante="secondary" sm onClick={actualizar}>
        Actualizar ahora
      </Boton>
    </div>
  )
}

const st = {
  barra: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 9000,
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '12px 20px',
    background: '#1e3a8a', color: '#eff6ff', borderTop: '3px solid #3b82f6',
    boxShadow: '0 -4px 16px rgba(0,0,0,0.25)',
  },
  icono: { fontSize: 22 },
  titulo: { fontSize: text.body, fontWeight: 700 },
  detalle: { fontSize: text.sec, color: '#bfdbfe', marginTop: 2 },
}
